// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SectionComponent,
  Input,
  DatePicker,
  Icon,
  ComboBox,
} from "@components";

import * as selectors from "@modules/shared/selectors";
import actionCreator from "@modules/shared/actionCreator";
import type { QuestionAnswersType } from "@modules/shared/reducer/types";
import type { OptionType } from "@components";
type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
}>;

type PropsFromState = $Exact<{
  referralProcesses: any,
}>;

type PropsFromDispatch = {
  toggleManualReferralModal: typeof actionCreator.ToggleManualReferralModal,
  postManualReferral: typeof actionCreator.PostManualReferral,
};

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
  t: Function,
}>;

type OwnState = $Exact<{
  selectedSupplier: Object,
  selectedReason: null | number,
  formQuestionsAnswers: Array<QuestionAnswersType>,
}>;

export class ManualReferralModal extends React.Component<Props, OwnState> {
  state = {
    selectedSupplier: {},
    selectedReason: null,
    formQuestionsAnswers: [],
  };

  get suppliers() {
    return this.props.referralProcesses.filter(process => {
      return !!(
        process.content &&
        process.content.isActive === true &&
        process.content.manualReferrals === true
      );
    });
  }
  get selectedSupplier() {
    return {
      value: this.state.selectedSupplier?.id,
      label: this.state.selectedSupplier?.content?.name,
    };
  }
  get reasons() {
    const selectedSupplier = this.state.selectedSupplier;
    if (selectedSupplier !== null && selectedSupplier.content) {
      return selectedSupplier.content.manualReferralsSettings.reasons
        .filter(reason => {
          return reason.isActive;
        })
        .map(reason => {
          return {
            value: reason.id,
            label: reason.reason,
          };
        });
    }
    return [];
  }
  isValid = () => {
    const { selectedSupplier, selectedReason } = this.state;
    if (!selectedSupplier.id || !selectedReason) return false;

    const allMandatoryQuestions = this.state.formQuestionsAnswers.filter(
      qa => qa.question.isMandatory,
    );
    if (!allMandatoryQuestions.length) return true;

    const allMandatoryAnswered = allMandatoryQuestions.every(qa => {
      if (Array.isArray(qa.answer.value)) {
        return qa.answer.value.length;
      }
      return qa.answer.value;
    });

    return allMandatoryAnswered;
  };
  selectSupplier = (selection: Object) => {
    const selectedSupplier = (this.suppliers || []).find(sup => {
      return sup.id === selection;
    });

    this.setState({
      selectedSupplier,
      selectedReason: null,
      formQuestionsAnswers: [],
    });
  };
  selectReason = (selectedReason: number) => {
    this.setState({
      selectedReason,
      formQuestionsAnswers:
        this.state.selectedSupplier &&
        this.state.selectedSupplier.content.managerReferralsSettings.formQuestions
          .filter(qa => qa.isActive)
          .map(qa => {
            return {
              question: {
                id: qa.id,
                isMandatory: qa.isMandatory,
                questionText: qa.questionText,
                questionType: qa.questionType,
                selectableValues: qa.selectableValues,
                type: qa.type,
              },
              answer: { value: null },
            };
          }),
    });
  };
  updateFreeText = (a: number) => (b: SyntheticEvent<HTMLInputElement>) => {
    this.updateAnswer(a)(b.currentTarget.value);
  };
  updateMultiSelect = (idx: number) => (val: OptionType) => {
    this.updateAnswer(idx)(val);
  };
  updateSingleSelect = (idx: number) => (val: OptionType) => {
    this.updateAnswer(idx)(val);
  };
  updateAnswer = (idx: number) => (value: any) => {
    let fqa = this.state.formQuestionsAnswers || [];
    fqa[idx].answer = { value };

    this.setState({
      ...this.state,
      formQuestionsAnswers: fqa,
    });
  };
  convertSelectData = (vals: Array<string>): Array<Object> => {
    return vals.map((d: string) => {
      return { value: d, label: d };
    });
  };
  generateQuestionField = (currentQuestion: any, questionIndex: number) => {
    const currentAnswer = this.state.formQuestionsAnswers[questionIndex].answer.value;
    switch (currentQuestion.questionType) {
      case "CONFIRMATION":
        return <div />;
      case "FREE_TEXT":
        return <Input value={currentAnswer || ""} onChange={this.updateFreeText(questionIndex)} />;
      case "DATE_PICK":
        return <DatePicker value={currentAnswer} onChange={this.updateAnswer(questionIndex)} />;
      case "MULTI_SELECT":
        return (
          <ComboBox
            onChange={this.updateMultiSelect(questionIndex)}
            options={this.convertSelectData(currentQuestion.selectableValues || [])}
            value={currentAnswer}
            isMulti
          />
        );
      case "SINGLE_SELECT":
        return (
          <ComboBox
            onChange={this.updateSingleSelect(questionIndex)}
            options={this.convertSelectData(currentQuestion.selectableValues || [])}
            value={currentAnswer ? { value: currentAnswer, label: currentAnswer } : null}
          />
        );
    }
    return true;
  };

  generateQuestions = (formQuestionsAnswers: Array<QuestionAnswersType>) => {
    //$FlowFixMe
    return formQuestionsAnswers.map((qa: QuestionAnswersType, idx: number) => {
      return (
        <div key={qa.question.id} className={"mb-4"}>
          {qa.question.questionText}
          {qa.question.isMandatory && (
            <Icon icon="asterisk" size="xs" className="text-warning ml-2" />
          )}
          {this.generateQuestionField(qa.question, idx)}
        </div>
      );
    });
  };
  submitForm = () => {
    const selectedReason =
      this.state.selectedSupplier &&
      this.state.selectedSupplier.content.manualReferralsSettings.reasons.find(
        reason => this.state.selectedReason && this.state.selectedReason === reason.id,
      );
    this.props.postManualReferral({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      formData: {
        id: this.state.selectedSupplier.id,
        reason: selectedReason,
        questionAnswers: this.state.formQuestionsAnswers,
      },
    });
  };

  render() {
    return (
      <Modal className="modal-normal">
        <ModalHeader
          toggle={this.props.toggleManualReferralModal}
          className="d-flex justify-content-between"
        >
          {this.props.t("Manual Referral")}
        </ModalHeader>
        <ModalBody>
          <SectionComponent
            title={<h5>{this.props.t("Select Supplier")}</h5>}
            wrapperClassName="flex-column"
            defaultBorder
          >
            <ComboBox
              onChange={this.selectSupplier}
              options={this.suppliers.map(process => {
                return {
                  value: process.id,
                  label: process.content.name,
                };
              })}
              value={this.selectedSupplier}
              withoutEmptyOption
            />
          </SectionComponent>

          <SectionComponent
            title={<h5>{this.props.t("Select Reason for Referral")}</h5>}
            wrapperClassName="flex-column"
            className="mt-4"
            defaultBorder
          >
            {this.state.selectedSupplier.id && (
              <ComboBox
                onChange={this.selectReason}
                options={this.reasons}
                value={this.state.selectedReason}
                withoutEmptyOption
              />
            )}
          </SectionComponent>

          <SectionComponent
            title={<h5>{this.props.t("REFERRAL_FORM")}</h5>}
            wrapperClassName="flex-column"
            className="mt-4"
            defaultBorder
          >
            {this.state.selectedReason !== null &&
              this.generateQuestions(this.state.formQuestionsAnswers)}
          </SectionComponent>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.toggleManualReferralModal}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="add_circle" disabled={!this.isValid()} onClick={this.submitForm}>
            {this.props.t("ADD")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: Object): PropsFromState => ({
  referralProcesses: selectors.getManualReferralProcesses(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  toggleManualReferralModal: actionCreator.ToggleManualReferralModal,
  postManualReferral: actionCreator.PostManualReferral,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(ManualReferralModal);
